// US-CUSTOM-01 -- Bucket configuration templates.

import {
  type AccountListEntry,
  type BucketTemplate,
  accountsList,
  bucketTemplateApply,
  bucketTemplateDelete,
  bucketTemplateSaveFromChild,
  bucketTemplatesList,
} from '@chorepop/core/client';
import { type Component, For, Show, createResource, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';
import { TransientBanner } from '../components/TransientBanner';

type Props = {
  sessionId: string;
  onBack: () => void;
};

export const BucketTemplatesScreen: Component<Props> = (props) => {
  const [templates, { refetch }] = createResource(
    () => props.sessionId,
    (sid) => bucketTemplatesList(sid).then((r) => (r.ok ? r.value : [])),
  );

  const [children] = createResource(async () => {
    const r = await accountsList();
    return r.ok ? r.value.filter((a) => a.roleCode === 'child') : ([] as AccountListEntry[]);
  });

  const [savingFrom, setSavingFrom] = createSignal<number | null>(null);
  const [newName, setNewName] = createSignal('');
  const [applyingId, setApplyingId] = createSignal<number | null>(null);
  const [applyChildId, setApplyChildId] = createSignal<number | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [flash, setFlash] = createSignal<string | null>(null);
  const [error, setError] = createSignal<string | null>(null);

  const handleSaveFrom = async () => {
    const cid = savingFrom();
    if (!cid || !newName().trim()) {
      setError('Choose a child and enter a name.');
      return;
    }
    setBusy(true);
    setError(null);
    const r = await bucketTemplateSaveFromChild(props.sessionId, cid, newName().trim());
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    setFlash('Template saved!');
    setSavingFrom(null);
    setNewName('');
    void refetch();
  };

  const handleApply = async () => {
    const tid = applyingId();
    const cid = applyChildId();
    if (!tid || !cid) {
      setError('Select a child first.');
      return;
    }
    setBusy(true);
    setError(null);
    const r = await bucketTemplateApply(props.sessionId, tid, cid);
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    setFlash(`Added ${r.value.bucketsAdded} new bucket${r.value.bucketsAdded !== 1 ? 's' : ''}.`);
    setApplyingId(null);
    setApplyChildId(null);
  };

  const handleDelete = async (t: BucketTemplate) => {
    if (!confirm(`Delete template "${t.templateName}"?`)) return;
    await bucketTemplateDelete(props.sessionId, t.templateId);
    void refetch();
  };

  return (
    <ActionScreen
      class="action-screen--bucket-templates"
      eyebrow="Bucket presets"
      title="Templates"
      onBack={props.onBack}
    >
      <p class="action-screen-intro">
        Save a child's bucket layout as a reusable template, then apply it to new children without
        re-entering the same structure. Applying never removes existing buckets or balances.
      </p>

      <Show when={error()}>
        <div class="form-error" role="alert">
          {error()}
        </div>
      </Show>
      <Show when={flash()}>
        {(message) => (
          <TransientBanner
            class="form-success bucket-template-flash"
            message={message()}
            testId="bucket-template-flash"
            onDismiss={() => setFlash(null)}
          />
        )}
      </Show>

      {/* Save from child */}
      <div class="form-card" style="margin-bottom: var(--space-4);">
        <h2 style="margin: 0 0 var(--space-3); font-size: 1rem;">Save current setup as template</h2>
        <div class="field-row--inline">
          <div>
            <label for="template-source-child">Child</label>
            <select
              id="template-source-child"
              value={savingFrom() ?? ''}
              onChange={(e) => setSavingFrom(Number(e.currentTarget.value) || null)}
            >
              <option value="">-- pick child --</option>
              <For each={children() ?? []}>
                {(c: AccountListEntry) => <option value={c.accountId}>{c.displayName}</option>}
              </For>
            </select>
          </div>
          <div>
            <label for="template-name">Template name</label>
            <input
              id="template-name"
              type="text"
              placeholder="e.g. Standard 3-bucket"
              value={newName()}
              onInput={(e) => setNewName(e.currentTarget.value)}
            />
          </div>
        </div>
        <div class="form-actions" style="margin-top: var(--space-3);">
          <button
            type="button"
            class="primary"
            disabled={busy() || !savingFrom() || !newName().trim()}
            onClick={handleSaveFrom}
          >
            Save template
          </button>
        </div>
      </div>

      {/* Template list */}
      <Show when={!templates.loading && (templates()?.length ?? 0) === 0}>
        <p class="settings-section-intro" style="color: var(--ink-2);">
          No templates yet. Save a child's bucket setup to get started.
        </p>
      </Show>

      <ul class="bucket-template-list">
        <For each={templates() ?? []}>
          {(t) => (
            <li class="bucket-template-card" data-testid={`template-${t.templateId}`}>
              <strong>{t.templateName}</strong>
              <div class="bucket-template-bar" aria-label={`${t.templateName} split`}>
                <For each={t.items}>
                  {(item) => (
                    <span
                      style={`--bucket-share: ${Math.max(item.allocationBps, 1)}; --bucket-color: ${item.displayColor ?? 'var(--brand)'}`}
                    >
                      <span class="sr-only">
                        {item.bucketName} {(item.allocationBps / 100).toFixed(0)}%
                      </span>
                    </span>
                  )}
                </For>
              </div>
              <div class="bucket-template-labels">
                <For each={t.items}>
                  {(item) => (
                    <span>
                      {item.bucketName} {(item.allocationBps / 100).toFixed(0)}%
                    </span>
                  )}
                </For>
              </div>

              <Show when={applyingId() === t.templateId}>
                <div class="routine-pause-panel">
                  <label for={`template-target-${t.templateId}`}>Apply to child:</label>
                  <select
                    id={`template-target-${t.templateId}`}
                    value={applyChildId() ?? ''}
                    onChange={(e) => setApplyChildId(Number(e.currentTarget.value) || null)}
                  >
                    <option value="">-- select child --</option>
                    <For each={children() ?? []}>
                      {(c: AccountListEntry) => (
                        <option value={c.accountId}>{c.displayName}</option>
                      )}
                    </For>
                  </select>
                  <div class="routine-pause-actions">
                    <button
                      type="button"
                      class="primary"
                      disabled={busy() || !applyChildId()}
                      onClick={handleApply}
                    >
                      Apply
                    </button>
                    <button type="button" class="ghost" onClick={() => setApplyingId(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              </Show>

              <div class="routine-row-actions">
                <button
                  type="button"
                  class="ghost"
                  disabled={busy()}
                  onClick={() => {
                    setApplyingId(t.templateId);
                    setApplyChildId(null);
                  }}
                >
                  Apply to child
                </button>
                <button
                  type="button"
                  class="ghost"
                  style="color: var(--color-danger);"
                  disabled={busy()}
                  onClick={() => handleDelete(t)}
                >
                  Delete
                </button>
              </div>
            </li>
          )}
        </For>
      </ul>
    </ActionScreen>
  );
};
